import React, { useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './IntroVideoUpload.module.sass';
import IntroVideoPreview from '../IntroVideoPreview';
import { getAuthToken } from '../../utils/authHelpers';
import {
  getIntroVideoFullUrl,
  getIntroVideoHlsUrl,
  getIntroVideoHoverPreviewUrl,
  getIntroVideoPosterUrl,
  normalizeIntroVideo,
} from '../../utils/cloudinaryVideo';

const MAX_DURATION_SECONDS = 30;
const MAX_FILE_SIZE = 60 * 1024 * 1024;
const ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

const getVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read video duration.'));
    };
    video.src = url;
  });

const uploadToCloudinary = (file, signatureData, onProgress) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', signatureData.apiKey);
    formData.append('timestamp', signatureData.timestamp);
    formData.append('signature', signatureData.signature);
    formData.append('public_id', signatureData.publicId);
    formData.append('eager', signatureData.eager);
    formData.append('overwrite', 'true');
    formData.append('invalidate', 'true');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${signatureData.cloudName}/video/upload`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          reject(new Error(data.error?.message || 'Upload failed.'));
        }
      } catch (error) {
        reject(error);
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed.'));
    xhr.send(formData);
  });

const IntroVideoUpload = ({
  className,
  value,
  onChange,
  currentVideoUrl,
  kind = 'intro_video',
  propertyId = null,
  title = 'Add a 30-second intro video',
  description = 'Profiles with intro videos get noticed faster and feel more trustworthy.',
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const video = useMemo(() => normalizeIntroVideo(value), [value]);
  const hasVideo = Boolean(video?.url || currentVideoUrl);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError('');

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please upload an MP4, MOV, or WEBM video.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('Intro videos must be 60MB or smaller.');
      return;
    }

    let duration = 0;
    try {
      duration = await getVideoDuration(file);
    } catch (durationError) {
      setError(durationError.message);
      return;
    }

    if (duration > MAX_DURATION_SECONDS + 0.25) {
      setError('Intro videos must be 30 seconds or shorter.');
      return;
    }

    if (kind === 'property_intro_video' && !propertyId) {
      setError('Save the property first, then upload a video.');
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const token = getAuthToken();
      let email = '';
      if (typeof window !== 'undefined') {
        try {
          const params = new URLSearchParams(window.location.search || '');
          email = params.get('email') || window.localStorage.getItem('postaway_email') || '';
        } catch (_) {}
      }
      const signBody = kind === 'property_intro_video'
        ? { kind, propertyId, email }
        : { kind: 'intro_video', email };
      const signResponse = await fetch('/api/cloudinary/sign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(signBody),
      });
      const signatureData = await signResponse.json();
      if (!signResponse.ok || !signatureData.ok) {
        throw new Error(signatureData.error === 'unauthorized' ? 'Please sign in before uploading a video.' : 'Could not prepare upload.');
      }

      const uploaded = await uploadToCloudinary(file, signatureData, setProgress);
      const publicId = uploaded.public_id || signatureData.publicId;
      const eager = Array.isArray(uploaded.eager) ? uploaded.eager : [];
      const nextVideo = {
        url: uploaded.secure_url || getIntroVideoFullUrl(publicId),
        hlsUrl: eager.find((item) => item.secure_url?.includes('.m3u8'))?.secure_url || getIntroVideoHlsUrl(publicId),
        posterUrl: eager.find((item) => item.secure_url?.match(/\.(jpg|jpeg)$/i))?.secure_url || getIntroVideoPosterUrl(publicId),
        previewUrl: eager.find((item) => item.secure_url?.match(/\.mp4/i))?.secure_url || getIntroVideoHoverPreviewUrl(publicId),
        publicId,
        duration: Number(duration.toFixed(2)),
      };

      onChange?.(nextVideo);
    } catch (uploadError) {
      setError(uploadError.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleRemove = () => {
    setError('');
    onChange?.(null);
  };

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>{title}</div>
          <div className={styles.copy}>{description}</div>
        </div>
        {hasVideo && (
          <button type="button" className={styles.removeButton} onClick={handleRemove}>
            Remove
          </button>
        )}
      </div>

      {hasVideo && (
        <div className={styles.preview}>
          <IntroVideoPreview
            mode="hero"
            fullUrl={video?.url || currentVideoUrl}
            hlsUrl={video?.hlsUrl}
            previewUrl={video?.previewUrl}
            posterUrl={video?.posterUrl}
          />
        </div>
      )}

      <input
        ref={inputRef}
        className={styles.input}
        type="file"
        accept="video/mp4,video/quicktime,video/webm"
        onChange={handleFile}
      />
      <button
        type="button"
        className={styles.uploadButton}
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? `Uploading ${progress}%` : hasVideo ? 'Replace Intro Video' : 'Upload Intro Video'}
      </button>
      <div className={styles.hint}>MP4, MOV, or WEBM. Max 30 seconds and 60MB.</div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default IntroVideoUpload;
